import React from "react";
import * as yup from "yup";
import { ValidationSchema } from "./or-form.types";

export const buildValidationSchema = (
  children: React.ReactNode,
): yup.ObjectSchema<Record<string, unknown>> | undefined => {
  const schemaFields: Record<string, yup.AnySchema> = {};

  const findFields = (children: React.ReactNode) => {
    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child)) {
        const props = child.props as {
          name?: string;
          validation?: ValidationSchema;
          children?: React.ReactNode;
        };

        // Check if it's a form field by looking for specific props
        if (props.name && props.validation) {
          const { name, validation } = props;
          if (validation.multiselect) {
            let schema = yup.array().of(yup.string());
            if (validation.required) {
              schema = schema
                .required(validation.required)
                .min(1, validation.required);
            }
            schemaFields[name] = schema;
            return;
          }

          let schema = yup.string();

          // Add required validation if specified
          if (validation.required) {
            schema = schema.required(validation.required);
          }

          // Apply validations based on whether field is required
          if (validation.required) {
            // Required fields: apply all validations
            if (validation.email)
              schema = schema.matches(
                /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/i,
                validation.email,
              );
            if (validation.min)
              schema = schema.min(validation.min.value, validation.min.message);
            if (validation.max)
              schema = schema.max(validation.max.value, validation.max.message);
            if (validation.pattern)
              schema = schema.matches(
                validation.pattern.value,
                validation.pattern.message,
              );
            if (validation.custom) {
              schema = schema.test("custom", "Invalid", (value) => {
                const result = validation.custom!(value);
                return result === undefined ? true : result === "";
              });
            }
          } else {
            // Optional fields: only validate if there's a value
            schema = schema.test("optional", "Invalid", function (value) {
              if (!value || value === "") return true; // Empty is always valid

              // Build validation schema dynamically
              let tempSchema = yup.string();
              if (validation.email)
                tempSchema = tempSchema.email(validation.email);
              if (validation.min)
                tempSchema = tempSchema.min(
                  validation.min.value,
                  validation.min.message,
                );
              if (validation.max)
                tempSchema = tempSchema.max(
                  validation.max.value,
                  validation.max.message,
                );
              if (validation.pattern)
                tempSchema = tempSchema.matches(
                  validation.pattern.value,
                  validation.pattern.message,
                );
              if (validation.custom) {
                tempSchema = tempSchema.test("custom", "Invalid", (val) => {
                  const result = validation.custom!(val);
                  return result === undefined ? true : result === "";
                });
              }

              try {
                tempSchema.validateSync(value);
                return true;
              } catch (err) {
                const errorMessage =
                  err instanceof Error ? err.message : "Invalid value";
                return this.createError({ message: errorMessage });
              }
            });
          }

          schemaFields[name] = schema;
        }

        if (props.children) findFields(props.children);
      }
    });
  };

  findFields(children);
  return Object.keys(schemaFields).length > 0
    ? yup.object(schemaFields)
    : undefined;
};
